/**
 * Performance & Scalability Utilities
 */

import { PerformanceMetadata, ExtendedArchiMateDiagram } from './extended-types';
import { ArchiMateElement, ArchiMateRelationship } from './types';

export function createPerformanceMetadata(): PerformanceMetadata {
  return {
    slo: {},
    scalability: {
      min: 1,
      max: 10,
      autoScale: false,
    },
    bottleneck: false,
  };
}

export function parseSLO(slo: string): { value: number; unit: string } | null {
  // Parse SLO strings like "p99 < 100ms", "99.9%", "1000 rps"
  const match = slo.match(/([<>]=?)\s*(\d+\.?\d*)\s*(\w+)/);
  if (match) {
    return { value: parseFloat(match[2]), unit: match[3] };
  }
  return null;
}

export function calculatePerformanceScore(metadata: PerformanceMetadata): number {
  let score = 100;

  // Deduct points for missing SLOs
  if (!metadata.slo.latency) score -= 15;
  if (!metadata.slo.availability) score -= 15;
  if (!metadata.slo.throughput) score -= 10;

  // Deduct for bottlenecks
  if (metadata.bottleneck) score -= 20;

  // Deduct if not auto-scaling
  if (!metadata.scalability.autoScale && metadata.scalability.max > 1) score -= 10;

  // Deduct if current load is high
  if (metadata.currentLoad) {
    if (metadata.currentLoad.cpu > 80) score -= 15;
    if (metadata.currentLoad.memory > 80) score -= 10;
  }

  return Math.max(0, score);
}

export function calculateDiagramPerformanceScore(diagram: ExtendedArchiMateDiagram): number {
  let totalScore = 0;
  let count = 0;

  diagram.elements.forEach((element) => {
    const perf = diagram.elementMetadata[element.id]?.performance;
    if (perf) {
      totalScore += calculatePerformanceScore(perf);
      count++;
    }
  });

  return count > 0 ? totalScore / count : 0;
}

export function detectBottlenecks(diagram: ExtendedArchiMateDiagram): Array<{
  element: ArchiMateElement;
  reason: string;
  severity: 'low' | 'medium' | 'high';
}> {
  const bottlenecks: Array<{
    element: ArchiMateElement;
    reason: string;
    severity: 'low' | 'medium' | 'high';
  }> = [];

  diagram.elements.forEach((element) => {
    const perf = diagram.elementMetadata[element.id]?.performance;

    // Check if manually marked as bottleneck
    if (perf?.bottleneck) {
      bottlenecks.push({
        element,
        reason: 'Manually identified as bottleneck',
        severity: 'high',
      });
    }

    // Check current load
    if (perf?.currentLoad) {
      if (perf.currentLoad.cpu > 90) {
        bottlenecks.push({
          element,
          reason: `High CPU usage: ${perf.currentLoad.cpu}%`,
          severity: 'high',
        });
      } else if (perf.currentLoad.cpu > 80) {
        bottlenecks.push({
          element,
          reason: `Elevated CPU usage: ${perf.currentLoad.cpu}%`,
          severity: 'medium',
        });
      }

      if (perf.currentLoad.memory > 90) {
        bottlenecks.push({
          element,
          reason: `High memory usage: ${perf.currentLoad.memory}%`,
          severity: 'high',
        });
      }
    }

    // Check scalability limits
    if (perf?.scalability && !perf.scalability.autoScale) {
      if (perf.scalability.max <= 1) {
        bottlenecks.push({
          element,
          reason: 'No horizontal scalability (max: 1 instance)',
          severity: 'medium',
        });
      }
    }

    // Check dependency fan-in (many elements depend on this one)
    const incomingRels = diagram.relationships.filter((rel) => rel.targetId === element.id);
    if (incomingRels.length > 10) {
      bottlenecks.push({
        element,
        reason: `High fan-in: ${incomingRels.length} dependencies`,
        severity: 'medium',
      });
    }
  });

  return bottlenecks.sort((a, b) => {
    const severityOrder = { high: 3, medium: 2, low: 1 };
    return severityOrder[b.severity] - severityOrder[a.severity];
  });
}

export function analyzeCriticalPath(
  diagram: ExtendedArchiMateDiagram,
  startElementId: string,
  endElementId: string
): {
  path: ArchiMateElement[];
  totalLatency: number;
  bottleneck: ArchiMateElement | null;
} | null {
  // Find shortest path using BFS
  const visited = new Set<string>();
  const queue: Array<{ elementId: string; path: string[] }> = [
    { elementId: startElementId, path: [startElementId] },
  ];

  while (queue.length > 0) {
    const { elementId, path } = queue.shift()!;

    if (elementId === endElementId) {
      // Found path, calculate metrics
      const elements = path
        .map((id) => diagram.elements.find((e) => e.id === id))
        .filter((e): e is ArchiMateElement => e !== undefined);

      let totalLatency = 0;
      let bottleneck: ArchiMateElement | null = null;
      let maxLatency = 0;

      elements.forEach((element) => {
        const perf = diagram.elementMetadata[element.id]?.performance;
        if (perf?.slo.latency) {
          const latencySlo = parseSLO(perf.slo.latency);
          if (latencySlo && latencySlo.unit === 'ms') {
            totalLatency += latencySlo.value;
            if (latencySlo.value > maxLatency) {
              maxLatency = latencySlo.value;
              bottleneck = element;
            }
          }
        }
      });

      return { path: elements, totalLatency, bottleneck };
    }

    if (visited.has(elementId)) continue;
    visited.add(elementId);

    // Find outgoing relationships
    const outgoing = diagram.relationships.filter((rel) => rel.sourceId === elementId);
    outgoing.forEach((rel) => {
      if (!visited.has(rel.targetId)) {
        queue.push({ elementId: rel.targetId, path: [...path, rel.targetId] });
      }
    });
  }

  return null; // No path found
}

export function getMissingSLOs(diagram: ExtendedArchiMateDiagram): Array<{
  element: ArchiMateElement;
  missingSLOs: string[];
}> {
  const missing: Array<{ element: ArchiMateElement; missingSLOs: string[] }> = [];

  // Only check application and technology layers
  const relevantElements = diagram.elements.filter(
    (e) => e.layer === 'application' || e.layer === 'technology'
  );

  relevantElements.forEach((element) => {
    const perf = diagram.elementMetadata[element.id]?.performance;
    const missingSLOs: string[] = [];

    if (!perf) {
      missingSLOs.push('No performance metadata defined');
    } else {
      if (!perf.slo.latency) missingSLOs.push('Latency SLO');
      if (!perf.slo.availability) missingSLOs.push('Availability SLO');
      if (!perf.slo.throughput) missingSLOs.push('Throughput SLO');
    }

    if (missingSLOs.length > 0) {
      missing.push({ element, missingSLOs });
    }
  });

  return missing;
}

export function generatePerformanceReport(diagram: ExtendedArchiMateDiagram): string {
  let report = `# Performance & Scalability Report\n\n`;
  report += `## ${diagram.name}\n\n`;
  report += `**Generated:** ${new Date().toISOString().split('T')[0]}\n\n`;

  // Overall score
  const score = calculateDiagramPerformanceScore(diagram);
  report += `## Overall Performance Score: ${score.toFixed(1)}/100\n\n`;

  // Bottlenecks
  const bottlenecks = detectBottlenecks(diagram);
  report += `## Bottlenecks (${bottlenecks.length})\n\n`;
  if (bottlenecks.length === 0) {
    report += `No performance bottlenecks detected.\n\n`;
  } else {
    bottlenecks.forEach(({ element, reason, severity }) => {
      const emoji = { high: '🔴', medium: '🟡', low: '🟢' }[severity];
      report += `${emoji} **${element.name}**: ${reason}\n`;
    });
    report += `\n`;
  }

  // Missing SLOs
  const missingSLOs = getMissingSLOs(diagram);
  if (missingSLOs.length > 0) {
    report += `## Missing SLOs (${missingSLOs.length})\n\n`;
    missingSLOs.forEach(({ element, missingSLOs }) => {
      report += `- **${element.name}**: ${missingSLOs.join(', ')}\n`;
    });
    report += `\n`;
  }

  // Scalability analysis
  report += `## Scalability Analysis\n\n`;
  const scalableElements = diagram.elements.filter(
    (e) => diagram.elementMetadata[e.id]?.performance?.scalability.autoScale
  );
  const nonScalableElements = diagram.elements.filter((e) => {
    const perf = diagram.elementMetadata[e.id]?.performance;
    return perf && !perf.scalability.autoScale && perf.scalability.max <= 1;
  });

  report += `- **Auto-scaling enabled:** ${scalableElements.length} elements\n`;
  report += `- **Non-scalable:** ${nonScalableElements.length} elements\n\n`;

  if (nonScalableElements.length > 0) {
    report += `Non-scalable components:\n`;
    nonScalableElements.forEach((element) => {
      report += `- ${element.name}\n`;
    });
  }

  return report;
}

export function getPerformanceHeatmapData(
  diagram: ExtendedArchiMateDiagram
): Record<string, number> {
  const heatmap: Record<string, number> = {};

  diagram.elements.forEach((element) => {
    const perf = diagram.elementMetadata[element.id]?.performance;
    const score = perf ? calculatePerformanceScore(perf) : 100;
    heatmap[element.id] = score;
  });

  return heatmap;
}

export function getPerformanceColor(score: number): string {
  if (score >= 80) return '#10b981'; // green
  if (score >= 60) return '#eab308'; // yellow
  if (score >= 40) return '#f97316'; // orange
  return '#ef4444'; // red
}

export function estimateCapacity(
  element: ArchiMateElement,
  metadata: PerformanceMetadata
): {
  currentCapacity: number;
  maxCapacity: number;
  headroom: number;
  canScale: boolean;
} {
  const currentCapacity = metadata.currentLoad?.rps || 0;

  // Estimate max capacity based on scalability settings
  const instanceCapacity = metadata.slo.throughput
    ? parseFloat(metadata.slo.throughput.split(' ')[0])
    : 1000; // default

  const maxCapacity = instanceCapacity * metadata.scalability.max;
  const headroom = maxCapacity - currentCapacity;
  const canScale = metadata.scalability.autoScale || metadata.scalability.max > 1;

  return { currentCapacity, maxCapacity, headroom, canScale };
}

export function exportPerformanceToCSV(diagram: ExtendedArchiMateDiagram): string {
  let csv =
    'Element,Layer,Latency SLO,Availability SLO,Throughput SLO,Min Instances,Max Instances,Auto Scale,Bottleneck,Performance Score\n';

  diagram.elements.forEach((element) => {
    const perf = diagram.elementMetadata[element.id]?.performance;
    if (perf) {
      csv += `"${element.name}",`;
      csv += `${element.layer},`;
      csv += `"${perf.slo.latency || ''}",`;
      csv += `"${perf.slo.availability || ''}",`;
      csv += `"${perf.slo.throughput || ''}",`;
      csv += `${perf.scalability.min},`;
      csv += `${perf.scalability.max},`;
      csv += `${perf.scalability.autoScale ? 'Yes' : 'No'},`;
      csv += `${perf.bottleneck ? 'Yes' : 'No'},`;
      csv += `${calculatePerformanceScore(perf).toFixed(1)}\n`;
    }
  });

  return csv;
}
